// Phase 5 (web revival) — Stream C.
//
// Media surface for photos AND videos. Chrome (back button, actions,
// prev/next, keyboard nav, info drawer) lives in pages/AssetDetail.tsx —
// this component only loads bytes and renders them.
//
// WHICH bytes is decided by lib/media.ts: browsers decode maybe half of
// what a decade of iPhones produced, so anything they can't read (HEIC,
// HEIC sequences, Sony ARW and friends, HEVC in .mov) renders from the
// server's full-resolution `alternate_transcoded` twin instead. The old
// behaviour — hand the browser the original and swap to the 1024px
// thumbnail once it failed to decode — meant every HEIC in the library
// displayed as a thumbnail on Chrome and Firefox. The thumbnail is still
// the last-resort fallback, but it should now be genuinely rare.
//
// Resource bytes at /v1/assets/{id}/resources/{rid} require the Bearer
// header, so we can't drop the URL straight into `<img src>` — instead we
// prefetch into a blob URL via api.prefetchAuthedImage. For video that
// costs progressive playback (the whole file lands before the first
// frame); acceptable at family-clip sizes, and the fix is a signed
// query-token on the resource route, not a change here.
//
// Live Photos: ~4.7k assets carry a paired motion clip
// (PHAssetResourceType 9/10). Press and hold the photo — or tap the LIVE
// badge — to play it, Apple-style. The clip is fetched lazily on first
// play, never on mount.
//
// The uploaded clip is HEVC in a QuickTime container, which only Safari
// decodes, so what actually gets played is the server's H.264/MP4 twin
// (`livePhotoPlaybackSource`). When there is no twin yet the original is
// tried anyway — it works in Safari — and `onMotionError` catches the
// browsers where it doesn't, un-hiding the still instead of leaving the
// viewer on a blank frame.
//
// Rotation: `display_rotation` is applied here as a CSS transform (the
// stored original is never rewritten). A quarter turn also has to swap
// the element's max-width/max-height, because a CSS transform doesn't
// change the layout box — hence the ResizeObserver on the container.

import { useCallback, useEffect, useRef, useState } from 'react';
import { TransformComponent, TransformWrapper } from 'react-zoom-pan-pinch';
import { prefetchAuthedImage } from '../../api/client';
import type { AssetDetailDTO } from '../../api/types';
import {
  livePhotoMotion,
  livePhotoPlaybackSource,
  pickDisplaySource,
  resourcePath,
  rotationStyle,
  useElementBox,
} from '../../lib/media';

interface AssetViewerProps {
  asset: AssetDetailDTO;
  /// Reports whether the photo is currently zoomed in (scale > 1). The
  /// lightbox uses this to gate touch swipe-to-navigate: a horizontal drag
  /// while zoomed is a pan, not a photo change. Stays false for videos.
  onZoomChange?: (zoomed: boolean) => void;
}

/// Hold this long before the Live Photo starts playing. Short enough to
/// feel like Apple's press-and-hold, long enough that a tap-to-reveal-chrome
/// or the start of a swipe doesn't trigger it.
const LIVE_HOLD_MS = 350;
/// Moving this far during the hold means the user is swiping or panning.
const HOLD_CANCEL_PX = 10;

export function AssetViewer({ asset, onZoomChange }: AssetViewerProps) {
  const [src, setSrc] = useState<string | null>(null);
  const [usingFallback, setUsingFallback] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Container size drives the max dimensions of a quarter-turned element.
  const [boxRef, box] = useElementBox<HTMLDivElement>();

  // Pure + cheap, so recomputing every render is fine; the effect below
  // depends on the primitive path/rotation it yields, not on this object
  // (an object identity would refire on every refetch and flicker the
  // image).
  const source = pickDisplaySource(asset);
  const sourcePath = source?.path ?? null;
  const rotation = source?.rotation ?? asset.display_rotation ?? 0;
  // A rotated video gets its H.264 alternate re-baked in the background;
  // same resource id, different bytes. Refetch when that lands.
  const bakedRotation = source?.resource.baked_rotation ?? 0;
  // `motion` answers "is this a Live Photo?" (badge + gesture gating);
  // `motionSource` is the rendition to actually fetch.
  const motion = livePhotoMotion(asset);
  const motionSource = livePhotoPlaybackSource(asset);

  // ───────── primary bytes ─────────
  // Load the bearer-authed resource into a blob URL. Revoke on unmount /
  // src swap so we don't leak blobs across asset navigation.
  useEffect(() => {
    let cancelled = false;
    let createdURL: string | null = null;
    setSrc(null);
    setUsingFallback(false);
    setError(null);

    // No usable resource (rare — backed-only edit chain or pre-bake).
    // Fall back straight to the large thumbnail.
    const path = sourcePath ?? asset.thumbnails.large;
    const isThumbnail = sourcePath === null;

    void (async () => {
      try {
        const url = await prefetchAuthedImage(path);
        if (cancelled) {
          URL.revokeObjectURL(url);
          return;
        }
        createdURL = url;
        if (isThumbnail) setUsingFallback(true);
        setSrc(url);
      } catch (e) {
        if (!cancelled) setError(describe(e));
      }
    })();

    return () => {
      cancelled = true;
      if (createdURL) URL.revokeObjectURL(createdURL);
    };
  }, [asset.id, sourcePath, bakedRotation, asset.thumbnails.large]);

  // Last-resort fallback: the browser couldn't decode what we handed it
  // (a HEIC or RAW with no transcoded twin yet). Swap to the large JPEG
  // thumbnail — lower resolution, but it always renders. Tracked via
  // usingFallback so we don't ping-pong if the thumbnail also fails.
  const handleImageError = useCallback(() => {
    if (usingFallback) {
      console.warn('[AssetViewer] thumbnail fallback also failed', asset.id);
      setError('Image failed to load.');
      return;
    }
    console.info(
      `[AssetViewer] browser could not decode the full-size rendition for ` +
        `asset ${asset.id}; falling back to the large JPEG thumbnail.`,
    );
    void (async () => {
      try {
        const url = await prefetchAuthedImage(asset.thumbnails.large);
        // Revoke the previous blob URL before swapping.
        if (src) URL.revokeObjectURL(src);
        setUsingFallback(true);
        setSrc(url);
      } catch (e) {
        setError(describe(e));
      }
    })();
  }, [asset.id, asset.thumbnails.large, src, usingFallback]);

  // ───────── Live Photo motion clip ─────────
  const [motionSrc, setMotionSrc] = useState<string | null>(null);
  const [livePlaying, setLivePlaying] = useState(false);
  const [motionFailed, setMotionFailed] = useState(false);
  const motionRef = useRef<HTMLVideoElement>(null);
  const motionURLRef = useRef<string | null>(null);

  // Drop the cached clip when the asset changes.
  useEffect(() => {
    return () => {
      if (motionURLRef.current) {
        URL.revokeObjectURL(motionURLRef.current);
        motionURLRef.current = null;
      }
      setMotionSrc(null);
      setLivePlaying(false);
      setMotionFailed(false);
    };
  }, [asset.id]);

  // Decode failure, or a `play()` the browser refused. Un-hide the still at
  // once — the `<img>` is `invisible` while a clip is mounted, so without
  // this the viewer sits on an empty frame forever, which is exactly how a
  // corpus of undecodable clips stayed invisible as a bug. Latch it so
  // press-and-hold stops re-trying something this browser can't play.
  const onMotionError = useCallback(() => {
    setMotionFailed(true);
    setLivePlaying(false);
  }, []);

  const startLive = useCallback(() => {
    if (!motionSource || motionFailed) return;
    setLivePlaying(true);
    if (motionURLRef.current) {
      const el = motionRef.current;
      if (el) {
        el.currentTime = 0;
        void el.play().catch(onMotionError);
      }
      return;
    }
    void (async () => {
      try {
        const url = await prefetchAuthedImage(
          resourcePath(asset.id, motionSource.id),
        );
        motionURLRef.current = url;
        setMotionSrc(url);
      } catch {
        setLivePlaying(false);
      }
    })();
  }, [asset.id, motionSource, motionFailed, onMotionError]);

  const stopLive = useCallback(() => {
    setLivePlaying(false);
    const el = motionRef.current;
    if (el) {
      el.pause();
      el.currentTime = 0;
    }
  }, []);

  // Press-and-hold. Bubble-phase on purpose: the lightbox's swipe handlers
  // are capture-phase, so both see the gesture and neither has to know
  // about the other. Any real movement cancels the hold, which keeps a
  // swipe from turning into a playback.
  const holdTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const holdOrigin = useRef<{ x: number; y: number } | null>(null);

  const cancelHold = useCallback(() => {
    if (holdTimer.current) {
      clearTimeout(holdTimer.current);
      holdTimer.current = null;
    }
    holdOrigin.current = null;
  }, []);

  const onPointerDown = useCallback(
    (e: React.PointerEvent) => {
      if (!motion || motionFailed || livePlaying) return;
      holdOrigin.current = { x: e.clientX, y: e.clientY };
      holdTimer.current = setTimeout(() => {
        holdTimer.current = null;
        startLive();
      }, LIVE_HOLD_MS);
    },
    [motion, motionFailed, livePlaying, startLive],
  );

  const onPointerMove = useCallback(
    (e: React.PointerEvent) => {
      const origin = holdOrigin.current;
      if (!origin) return;
      if (Math.hypot(e.clientX - origin.x, e.clientY - origin.y) > HOLD_CANCEL_PX) {
        cancelHold();
      }
    },
    [cancelHold],
  );

  const onPointerRelease = useCallback(() => {
    cancelHold();
    if (livePlaying) stopLive();
  }, [cancelHold, livePlaying, stopLive]);

  useEffect(() => () => cancelHold(), [cancelHold]);

  // Surface the live zoom scale to the lightbox (cheap — the parent just
  // stores it in a ref). >1.05 means a finger drag should pan, not swipe.
  const handleTransformed = useCallback(
    (_ref: unknown, state: { scale: number }) => {
      onZoomChange?.(state.scale > 1.05);
    },
    [onZoomChange],
  );

  const mediaStyle = rotationStyle(rotation, box);

  return (
    <div ref={boxRef} className="relative h-full w-full bg-black">
      {asset.kind === 'video' ? (
        // Video render: no zoom wrapper (browser <video> handles its own
        // controls); blob URL drives src. The thumbnail fallback doesn't
        // apply — a video decode failure means no playable rendition
        // exists, and a still poster would just look like a bug.
        <div className="flex h-full w-full items-center justify-center">
          {src ? (
            <video
              key={asset.id}
              src={src}
              controls
              playsInline
              preload="metadata"
              style={mediaStyle}
              className="media-fade-in max-h-full max-w-full select-none object-contain"
            />
          ) : error ? (
            <div className="px-6 text-center text-sm text-red-300">
              {error}
            </div>
          ) : (
            <LoadingSpinner />
          )}
        </div>
      ) : (
        <TransformWrapper
          minScale={1}
          maxScale={8}
          initialScale={1}
          wheel={{ step: 0.1 }}
          doubleClick={{ mode: 'toggle' }}
          onTransformed={handleTransformed}
          // Re-mount on asset change so pan/zoom resets.
          key={asset.id}
        >
          <TransformComponent
            wrapperClass="!h-full !w-full"
            contentClass="!h-full !w-full flex items-center justify-center"
          >
            <div
              className="flex h-full w-full items-center justify-center"
              onPointerDown={onPointerDown}
              onPointerMove={onPointerMove}
              onPointerUp={onPointerRelease}
              onPointerLeave={onPointerRelease}
              onPointerCancel={onPointerRelease}
            >
              {src ? (
                <>
                  <img
                    src={src}
                    alt={asset.caption ?? ''}
                    onError={handleImageError}
                    draggable={false}
                    style={mediaStyle}
                    className={`media-fade-in max-h-full max-w-full select-none object-contain ${
                      livePlaying && motionSrc ? 'invisible' : ''
                    }`}
                  />
                  {motionSrc && livePlaying && (
                    <video
                      ref={motionRef}
                      src={motionSrc}
                      autoPlay
                      muted
                      playsInline
                      onEnded={stopLive}
                      onError={onMotionError}
                      style={mediaStyle}
                      className="absolute max-h-full max-w-full select-none object-contain"
                    />
                  )}
                </>
              ) : error ? (
                <div className="px-6 text-center text-sm text-red-300">
                  {error}
                </div>
              ) : (
                <LoadingSpinner />
              )}
            </div>
          </TransformComponent>
        </TransformWrapper>
      )}

      {/* LIVE badge — also the tap target, since press-and-hold isn't
          discoverable and doesn't survive every touch stack. */}
      {motion && src && (
        <button
          type="button"
          onClick={() => (livePlaying ? stopLive() : startLive())}
          disabled={motionFailed}
          title={
            motionFailed
              ? "This browser can't play this Live Photo's motion clip"
              : undefined
          }
          aria-label={
            motionFailed
              ? "Live Photo motion clip unavailable in this browser"
              : livePlaying
                ? 'Stop Live Photo'
                : 'Play Live Photo'
          }
          aria-pressed={livePlaying}
          className={`absolute left-1/2 top-3 z-10 flex -translate-x-1/2 items-center gap-1.5 rounded-full px-2.5 py-1 text-[11px] font-medium tracking-wide backdrop-blur-sm transition-colors ${
            motionFailed
              ? 'cursor-not-allowed bg-black/30 text-neutral-500'
              : livePlaying
                ? 'bg-white/90 text-neutral-900'
                : 'bg-black/45 text-neutral-200 hover:bg-black/70 hover:text-white'
          }`}
        >
          <svg width="12" height="12" viewBox="0 0 24 24" aria-hidden fill="none">
            <circle cx="12" cy="12" r="3.2" fill="currentColor" />
            <circle cx="12" cy="12" r="7" stroke="currentColor" strokeWidth="1.6" strokeDasharray="3 2.4" />
          </svg>
          LIVE
        </button>
      )}

      {usingFallback && (
        <div className="absolute bottom-3 left-1/2 z-10 -translate-x-1/2 rounded-full bg-neutral-900/80 px-3 py-1 text-[11px] text-neutral-400 backdrop-blur-sm">
          Preview quality — original can't be decoded here
        </div>
      )}
    </div>
  );
}

function LoadingSpinner() {
  return (
    <div
      aria-label="Loading"
      className="size-6 animate-spin rounded-full border-2 border-neutral-800 border-t-neutral-400"
    />
  );
}

function describe(e: unknown): string {
  if (e instanceof Error) return e.message;
  return 'Failed to load image.';
}
